"use client";

/**
 * useHomeSections
 *
 * Single source of truth for which home-page sections are visible.
 * State is persisted to localStorage so the user's preferences survive page reloads.
 *
 * All instances of this hook (page.tsx + EditorOptionsMenu) stay in sync via
 * the "homeSettingsChanged" custom DOM event — same pattern as "recentNotesChanged".
 *
 * Future: replace the localStorage read/write with an API call to sync
 * preferences across devices — the public API of this hook stays identical.
 */

import { useEffect, useState } from "react";

// ── Section registry ──────────────────────────────────────────────────────────
// Add new section IDs here as the home page grows.

export type HomeSectionId =
  | "greeting"
  | "recently-visited"
  | "learn"
  | "upcoming-events"
  | "suggested"
  | "trending"
  | "featured-templates";

/** Human-readable label for each section (used in the settings menu). */
export const HOME_SECTION_LABELS: Record<HomeSectionId, string> = {
  "greeting":           "Greeting",
  "recently-visited":   "Recently visited",
  "learn":              "Learn",
  "upcoming-events":    "Upcoming events",
  "suggested":          "Suggested for you",
  "trending":           "Trending",
  "featured-templates": "Featured templates",
};

/** Ordered list used to render the section toggle list in the settings menu. */
export const HOME_SECTION_IDS: HomeSectionId[] = [
  "greeting",
  "recently-visited",
  "learn",
  "upcoming-events",
  "suggested",
  "trending",
  "featured-templates",
];

const LS_KEY     = "nh-hidden-sections";
const SYNC_EVENT = "homeSettingsChanged";

// ── Persistence helpers ───────────────────────────────────────────────────────

function readHidden(): Set<HomeSectionId> {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = localStorage.getItem(LS_KEY);
    return new Set(raw ? (JSON.parse(raw) as HomeSectionId[]) : []);
  } catch {
    return new Set();
  }
}

function writeHidden(ids: Set<HomeSectionId>) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify([...ids]));
    // Notify all other instances of the hook on the same page
    window.dispatchEvent(new CustomEvent(SYNC_EVENT));
  } catch { /* ignore quota errors */ }
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export interface HomeSectionsAPI {
  /** Returns true if the section should be rendered */
  isVisible: (id: HomeSectionId) => boolean;
  /** Hide a section */
  hideSection: (id: HomeSectionId) => void;
  /** Un-hide a section */
  showSection: (id: HomeSectionId) => void;
  /** Toggle a section between visible and hidden */
  toggleSection: (id: HomeSectionId) => void;
  /** Set of all currently hidden section IDs */
  hiddenSections: Set<HomeSectionId>;
  /** Restore all sections to visible (useful for a "Reset" button in settings) */
  resetAll: () => void;
}

export function useHomeSections(): HomeSectionsAPI {
  const [hidden, setHidden] = useState<Set<HomeSectionId>>(readHidden);

  // Sync across instances (e.g. page.tsx ↔ EditorOptionsMenu)
  useEffect(() => {
    const handler = () => setHidden(readHidden());
    window.addEventListener(SYNC_EVENT, handler);
    return () => window.removeEventListener(SYNC_EVENT, handler);
  }, []);

  const persist = (next: Set<HomeSectionId>) => {
    setHidden(next);
    writeHidden(next);
  };

  return {
    isVisible:     (id) => !hidden.has(id),
    hideSection:   (id) => persist(new Set([...hidden, id])),
    showSection:   (id) => { const s = new Set(hidden); s.delete(id); persist(s); },
    toggleSection: (id) => {
      const s = new Set(hidden);
      s.has(id) ? s.delete(id) : s.add(id);
      persist(s);
    },
    hiddenSections: hidden,
    resetAll: () => persist(new Set()),
  };
}
